import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Music, Heart, Brain, Sparkles, Moon, Star } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

const SoundHealingRetreatsUK = () => {
  usePageMeta({
    title: "Sound Healing Retreats UK | Sound Bath & Gong Retreats",
    description: "Experience transformative sound healing retreats in the UK. Tibetan singing bowls, gong baths, crystal bowls combined with yoga and meditation.",
    canonical: "https://annelielily.com/retreats/sound-healing"
  });

  const instruments = [
    {
      name: "Tibetan Singing Bowls",
      description: "Ancient metal bowls that produce rich, harmonic tones when struck or played with a mallet. The vibrations penetrate deeply, releasing tension held in the body.",
      benefits: ["Grounding and calming", "Helps release physical tension", "Traditional healing tool"]
    },
    {
      name: "Crystal Singing Bowls",
      description: "Made from pure quartz crystal, these bowls produce clear, sustained tones that resonate with the body's energy centres. Many people feel the vibrations in specific chakras.",
      benefits: ["Clear, pure tones", "Chakra balancing", "Deeply penetrating vibrations"]
    },
    {
      name: "Gongs",
      description: "The gong creates a full-spectrum wash of sound that envelops you completely. Gong baths are known for inducing profound states of relaxation and sometimes vivid imagery.",
      benefits: ["Full-body immersion", "Deep theta state", "Powerful release"]
    },
    {
      name: "Chimes & Bells",
      description: "Delicate sounds that punctuate the sound bath, often used at the beginning and end to gently transition you in and out of the experience.",
      benefits: ["Gentle transitions", "Mental clarity", "Lightness"]
    }
  ];

  const benefits = [
    { icon: Brain, title: "Deep Relaxation", desc: "Sound frequencies guide your brainwaves into theta state — deeper than normal sleep" },
    { icon: Heart, title: "Emotional Release", desc: "Vibrations can unlock stored emotions, allowing gentle processing and release" },
    { icon: Sparkles, title: "Energy Clearing", desc: "Many report feeling 'lighter' as stagnant energy is cleared from the body" },
    { icon: Moon, title: "Better Sleep", desc: "The deep relaxation often continues, improving sleep quality for days or weeks" }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Sound Healing Retreats" currentSlug="retreats/sound-healing" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              Sound Healing Retreats UK
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Immerse yourself in the transformative power of sound. Our retreats combine 
              Tibetan singing bowls, gongs, and crystal bowls with yoga and meditation 
              for a deeply healing experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What is Sound Healing */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What is Sound Healing?
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Sound healing is an ancient practice that uses vibration and frequency to 
                bring the body, mind, and spirit into a state of harmony. During a sound 
                bath, you lie comfortably while waves of sound from instruments like singing 
                bowls, gongs, and chimes wash over you.
              </p>
              <p>
                <strong>You don't need to do anything</strong> — simply receive. The vibrations 
                do the work, guiding your brainwaves from busy beta state into the deeply 
                relaxed theta state (the same state experienced in deep meditation or just 
                before sleep).
              </p>
              <p>
                Many people describe sound baths as profoundly relaxing yet energising — 
                they emerge feeling rested, clear, and somehow lighter than before.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Benefits of Sound Healing Retreats
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {benefits.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/80 p-6 rounded-xl border border-gold/10"
              >
                <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Instruments */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Instruments Used in Sound Healing
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Our sound healing sessions use a variety of instruments, each with its own 
              unique qualities and effects.
            </p>
          </motion.div>

          <div className="space-y-6">
            {instruments.map((instrument, i) => (
              <motion.div
                key={instrument.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/80 p-6 rounded-2xl border border-gold/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-neutral-800 mb-2">{instrument.name}</h3>
                    <p className="text-neutral-600 text-sm mb-3">{instrument.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {instrument.benefits.map((benefit, j) => (
                        <span key={j} className="text-xs bg-gold/10 text-gold px-2 py-1 rounded-full">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sound Healing + Yoga */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Why Combine Sound Healing with Yoga?
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Sound healing and yoga are natural partners. <strong>Yoga prepares the body 
                and mind to receive</strong> — opening, stretching, and softening so the sound 
                vibrations can penetrate more deeply.
              </p>
              <p>
                In our retreats, we typically structure the day so that movement practices 
                come first, followed by sound healing when you're already in a relaxed, 
                receptive state. This combination amplifies the benefits of both practices.
              </p>
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10 mt-6">
                <h3 className="font-medium text-neutral-800 mb-3">A typical sound healing retreat day:</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span><strong>Morning:</strong> Kundalini yoga to awaken and energise</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span><strong>Midday:</strong> Rest, nourishing food, free time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span><strong>Afternoon:</strong> Yin yoga to soften and open</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span><strong>Closing:</strong> Yoga nidra followed by full sound bath</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What to Expect During a Sound Bath
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                You'll lie on a yoga mat with blankets and cushions to make yourself completely 
                comfortable. Your eyes will be closed (an eye mask helps), and your only job is 
                to receive.
              </p>
              <p>
                The facilitator will begin to play, layering different instruments to create 
                waves of sound that ebb and flow around you. <strong>Common experiences include:</strong>
              </p>
              <ul className="space-y-2">
                {[
                  "Deep physical relaxation — feeling heavy or floating",
                  "Vivid imagery, colours, or memories arising",
                  "Emotional release — tears are common and welcomed",
                  "Losing track of time — an hour can feel like minutes",
                  "Tingling or warmth in different parts of the body",
                  "Falling into a deep, dream-like state"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                There's no "right" experience — whatever happens for you is exactly what you need.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 p-8 rounded-2xl border border-gold/10 text-center"
          >
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-gold text-gold" />
              ))}
            </div>
            <p className="text-neutral-700 italic text-lg mb-6">
              "The sound bath was unlike anything I've experienced. I felt the vibrations 
              moving through my body, releasing tension I didn't even know I was holding. 
              I left feeling like I'd had the best sleep of my life, but I was awake for 
              all of it."
            </p>
            <p className="font-medium text-neutral-800">Sound Healing Retreat Guest</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Experience Sound Healing
            </h2>
            <p className="text-neutral-600 mb-6">
              Sound healing is included in all our retreat experiences. Explore upcoming 
              dates or get in touch to learn more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/retreats/yoga-retreats-uk" className="btn-primary">
                View Yoga Retreats UK <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/treatments" className="btn-secondary">
                Healing Sessions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-heading text-lg text-neutral-800 mb-4 text-center">Related Pages</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/retreats/what-to-expect" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">What to Expect</span>
              <p className="text-neutral-500 text-xs mt-1">First retreat guide</p>
            </Link>
            <Link to="/yoga-classes/meditation" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Meditation Classes</span>
              <p className="text-neutral-500 text-xs mt-1">Weekly sessions</p>
            </Link>
            <Link to="/womens-circles" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Women's Circles</span>
              <p className="text-neutral-500 text-xs mt-1">Monthly gatherings</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoundHealingRetreatsUK;
